import { Modal } from 'bootstrap';

function parseJsonScript(root, selector, fallback) {
    const node = root.querySelector(selector);

    if (!node) {
        return fallback;
    }

    try {
        return JSON.parse(node.textContent || JSON.stringify(fallback));
    } catch {
        return fallback;
    }
}

function escapeHtml(value) {
    return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}

export class ImagePicker {
    constructor(root) {
        this.root = root;
        this.multiple = root.dataset.multiple === 'true';
        this.libraryUrl = root.dataset.libraryUrl;
        this.uploadUrl = root.dataset.uploadUrl;
        this.deleteUrlTemplate = root.dataset.deleteUrlTemplate;
        this.hiddenInputName = root.dataset.hiddenInputName || (this.multiple ? 'image_media_ids' : 'image_media_id');
        this.readyLabel = root.dataset.readyLabel || 'Image ready';
        this.readyHint = root.dataset.readyHint || 'Click browse to swap it';
        this.emptyLabel = root.dataset.emptyLabel || (this.multiple ? 'No images selected' : 'No image selected');
        this.triggerText = root.dataset.triggerText || (this.multiple ? 'Upload or choose images' : 'Upload or choose an image');
        this.helperText = root.dataset.helperText || (this.multiple ? 'JPG / PNG / WebP' : '1600x1000 - JPG / PNG / WebP');
        this.modalTitle = root.dataset.modalTitle || (this.multiple ? 'Choose images' : 'Choose an image');
        this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';

        this.selectedImages = this.multiple
            ? parseJsonScript(root, '[data-picker-initial]', [])
            : [];
        this.selectedImage = this.multiple
            ? null
            : parseJsonScript(root, '[data-picker-initial]', null);

        this.images = [];
        this.searchTerm = '';

        this.hiddenInput = root.querySelector(`input[name="${this.hiddenInputName}"]`);
        this.preview = root.querySelector('[data-picker-preview]');
        this.nameLabel = root.querySelector('[data-picker-name]');
        this.triggerButton = root.querySelector('[data-picker-trigger]');
        this.replaceButton = root.querySelector('[data-picker-replace]');
        this.removeButton = root.querySelector('[data-picker-remove]');
        this.spinner = root.querySelector('[data-picker-spinner]');
        this.errorBox = root.querySelector('[data-picker-error]');

        this.ensureModal();
        this.bindEvents();
        this.render();
        this.root.imagePicker = this;
    }

    ensureModal() {
        const modalId = this.root.id ? `${this.root.id}Modal` : `imagePickerModal${Math.random().toString(36).slice(2, 8)}`;

        if (!document.getElementById(modalId)) {
            const wrapper = document.createElement('div');
            wrapper.innerHTML = `
                <div class="modal fade" id="${modalId}" tabindex="-1" aria-hidden="true">
                    <div class="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered">
                        <div class="modal-content" style="background:#131314; color:#eceae3; border:1px solid #2b2b2d;">
                            <div class="modal-header border-bottom" style="border-color:#2b2b2d !important;">
                                <div>
                                    <div class="font-mono text-faint text-uppercase" style="font-size:.625rem; letter-spacing:.12em;">Media library</div>
                                    <h2 class="h5 m-0">${escapeHtml(this.modalTitle)}</h2>
                                </div>
                                <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>
                            <div class="modal-body p-4">
                                <div class="d-flex flex-column flex-lg-row gap-3 mb-4">
                                    <div class="flex-grow-1">
                                        <input type="search" class="form-control" placeholder="Search uploaded images..." data-picker-library-search>
                                    </div>
                                    <div class="d-flex align-items-center gap-2">
                                        <button type="button" class="btn btn-sm btn-primary pill" data-picker-library-upload>Upload image</button>
                                        <input type="file" accept="image/*" class="d-none" data-picker-library-input>
                                        <div class="spinner-border spinner-border-sm text-accent d-none" role="status" data-picker-library-spinner></div>
                                    </div>
                                </div>
                                <div class="alert alert-danger d-none py-2 px-3 mb-3" role="alert" data-picker-library-error></div>
                                <div class="row g-3" data-picker-library-grid></div>
                                <div class="text-center py-5 text-faint d-none" data-picker-library-empty>No images found.</div>
                            </div>
                            <div class="modal-footer border-top d-flex justify-content-between" style="border-color:#2b2b2d !important;">
                                <span class="font-mono text-faint" style="font-size:.6875rem;" data-picker-library-count></span>
                                <div class="d-flex gap-2">
                                    <button type="button" class="btn btn-sm btn-ghost pill" data-bs-dismiss="modal">Close</button>
                                    <button type="button" class="btn btn-sm btn-primary pill ${this.multiple ? '' : 'd-none'}" data-picker-library-apply>Use selected images</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            `;

            document.body.appendChild(wrapper.firstElementChild);
        }

        this.modalElement = document.getElementById(modalId);
        this.modal = new Modal(this.modalElement);
        this.librarySearchInput = this.modalElement.querySelector('[data-picker-library-search]');
        this.libraryUploadButton = this.modalElement.querySelector('[data-picker-library-upload]');
        this.libraryInput = this.modalElement.querySelector('[data-picker-library-input]');
        this.librarySpinner = this.modalElement.querySelector('[data-picker-library-spinner]');
        this.libraryError = this.modalElement.querySelector('[data-picker-library-error]');
        this.libraryGrid = this.modalElement.querySelector('[data-picker-library-grid]');
        this.libraryEmpty = this.modalElement.querySelector('[data-picker-library-empty]');
        this.libraryCount = this.modalElement.querySelector('[data-picker-library-count]');
        this.applyButton = this.modalElement.querySelector('[data-picker-library-apply]');
    }

    bindEvents() {
        this.triggerButton?.addEventListener('click', () => this.openLibrary());
        this.replaceButton?.addEventListener('click', () => this.openLibrary());
        this.removeButton?.addEventListener('click', () => this.clearSelection());
        this.libraryUploadButton?.addEventListener('click', () => this.libraryInput?.click());
        this.libraryInput?.addEventListener('change', (event) => this.handleUpload(event.target.files?.[0]));
        this.librarySearchInput?.addEventListener('input', () => {
            this.searchTerm = this.librarySearchInput.value.trim();
            this.loadLibrary();
        });
        this.applyButton?.addEventListener('click', () => this.modal.hide());
    }

    getSelection() {
        return this.multiple ? this.selectedImages : this.selectedImage;
    }

    setSelection(selection) {
        if (this.multiple) {
            this.selectedImages = Array.isArray(selection) ? selection : [];
        } else {
            this.selectedImage = selection;
        }
    }

    selectedCount() {
        return this.multiple ? this.selectedImages.length : (this.selectedImage ? 1 : 0);
    }

    isSelected(imageId) {
        if (this.multiple) {
            return this.selectedImages.some((image) => image.id === imageId);
        }

        return this.selectedImage?.id === imageId;
    }

    dispatchChange() {
        this.root.dispatchEvent(new CustomEvent('image-picker:change', {
            bubbles: true,
            detail: { image: this.getSelection(), picker: this },
        }));
    }

    setBusy(isBusy) {
        this.spinner?.classList.toggle('d-none', !isBusy);
        [this.triggerButton, this.replaceButton, this.removeButton].forEach((button) => {
            if (button) {
                button.disabled = isBusy;
            }
        });
    }

    setLibraryBusy(isBusy) {
        this.librarySpinner?.classList.toggle('d-none', !isBusy);
        [this.libraryUploadButton, this.triggerButton, this.replaceButton, this.applyButton].forEach((button) => {
            if (button) {
                button.disabled = isBusy;
            }
        });
    }

    setError(message) {
        if (!this.errorBox) {
            return;
        }

        this.errorBox.textContent = message;
        this.errorBox.classList.toggle('d-none', !message);
    }

    setLibraryError(message) {
        if (!this.libraryError) {
            return;
        }

        this.libraryError.textContent = message;
        this.libraryError.classList.toggle('d-none', !message);
    }

    syncHiddenInput() {
        if (!this.hiddenInput) {
            return;
        }

        if (this.multiple) {
            this.hiddenInput.value = JSON.stringify(this.selectedImages.map((image) => image.id));
        } else {
            this.hiddenInput.value = this.selectedImage?.id ?? '';
        }
    }

    renderSinglePreview() {
        if (!this.selectedImage) {
            this.preview.innerHTML = '';
            if (this.triggerButton) {
                this.preview.appendChild(this.triggerButton);
            }

            return;
        }

        this.preview.innerHTML = `
            <img src="${this.selectedImage.thumb_url || this.selectedImage.url}" alt="" style="width:100%; height:100%; object-fit:cover; display:block;">
            <div class="position-absolute start-0 end-0 bottom-0 d-flex align-items-center justify-content-between px-3 py-2" style="background:linear-gradient(180deg, rgba(13,13,14,0) 0%, rgba(13,13,14,.86) 100%);">
                <span class="font-mono text-uppercase" style="font-size:.625rem; letter-spacing:.12em; color:#eceae3;">${escapeHtml(this.readyLabel)}</span>
                <span class="font-mono text-faint" style="font-size:.625rem;">${escapeHtml(this.readyHint)}</span>
            </div>
        `;
    }

    renderMultiplePreview() {
        if (this.selectedImages.length === 0) {
            this.preview.innerHTML = '';
            if (this.triggerButton) {
                this.preview.appendChild(this.triggerButton);
            }

            return;
        }

        const featured = this.selectedImages[0];
        const thumbnails = this.selectedImages.slice(0, 4).map((image) => `
            <div class="col-3">
                <div style="aspect-ratio:1; border-radius:8px; overflow:hidden; border:1px solid #2b2b2d;">
                    <img src="${image.thumb_url || image.url}" alt="" style="width:100%; height:100%; object-fit:cover; display:block;">
                </div>
            </div>
        `).join('');

        this.preview.innerHTML = `
            <div class="p-3 h-100 d-flex flex-column" style="background:#0d0d0e;">
                <div class="position-relative overflow-hidden rounded-3 mb-3" style="aspect-ratio:16/8; border:1px solid #2b2b2d;">
                    <img src="${featured.thumb_url || featured.url}" alt="" style="width:100%; height:100%; object-fit:cover; display:block;">
                    <div class="position-absolute start-0 end-0 bottom-0 d-flex align-items-center justify-content-between px-3 py-2" style="background:linear-gradient(180deg, rgba(13,13,14,0) 0%, rgba(13,13,14,.86) 100%);">
                        <span class="font-mono text-uppercase" style="font-size:.625rem; letter-spacing:.12em; color:#eceae3;">${escapeHtml(this.readyLabel)}</span>
                        <span class="font-mono text-faint" style="font-size:.625rem;">${escapeHtml(this.readyHint)}</span>
                    </div>
                </div>
                <div class="row g-2">${thumbnails}</div>
            </div>
        `;
    }

    render() {
        this.syncHiddenInput();

        if (this.multiple) {
            const count = this.selectedImages.length;

            this.nameLabel.textContent = count > 0 ? `${count} image${count === 1 ? '' : 's'} selected` : this.emptyLabel;
            this.renderMultiplePreview();
        } else {
            this.nameLabel.textContent = this.selectedImage
                ? this.selectedImage.file_name || this.selectedImage.name || 'Selected image'
                : this.emptyLabel;
            this.renderSinglePreview();
        }

        const hasSelection = this.selectedCount() > 0;
        this.replaceButton?.classList.toggle('d-none', !hasSelection);
        this.removeButton?.classList.toggle('d-none', !hasSelection);
        this.triggerButton?.classList.toggle('d-none', hasSelection);

        if (this.libraryCount) {
            this.libraryCount.textContent = hasSelection
                ? `${this.selectedCount()} selected`
                : 'Nothing selected';
        }

        this.dispatchChange();
    }

    toggleImageSelection(image) {
        if (!this.multiple) {
            this.selectedImage = image;
            this.render();
            this.modal.hide();
            return;
        }

        if (this.isSelected(image.id)) {
            this.selectedImages = this.selectedImages.filter((selected) => selected.id !== image.id);
        } else {
            this.selectedImages = [...this.selectedImages, image];
        }

        this.render();
        this.renderLibrary();
    }

    renderLibrary() {
        this.libraryGrid.innerHTML = this.images.map((image) => {
            const isSelected = this.isSelected(image.id);

            return `
                <div class="col-6 col-md-4 col-xl-3">
                    <div class="position-relative rounded-3 overflow-hidden h-100 ${isSelected ? 'border border-2 border-warning' : 'border'}" style="border-color:${isSelected ? '#eb6d21' : '#2b2b2d'} !important; background:#0d0d0e;">
                        <button type="button" class="btn p-0 border-0 w-100 text-start" data-picker-select-id="${image.id}" style="background:transparent; color:inherit;">
                            <div style="aspect-ratio:1; background:#111;">
                                <img src="${image.thumb_url || image.url}" alt="" style="width:100%; height:100%; object-fit:cover; display:block;">
                            </div>
                            <div class="p-2">
                                <div class="d-flex align-items-center justify-content-between gap-2 mb-1">
                                    <span class="badge text-bg-dark border" style="border-color:#2b2b2d !important;">${escapeHtml(image.source_label || 'Image')}</span>
                                    ${isSelected ? '<span class="font-mono" style="font-size:.625rem; color:#eb6d21;">Selected</span>' : ''}
                                </div>
                                <div class="text-truncate" style="font-size:.75rem;">${escapeHtml(image.file_name || image.name || 'Image')}</div>
                            </div>
                        </button>
                        ${image.can_delete ? `
                            <button type="button" class="btn btn-sm position-absolute top-0 end-0 m-2" data-picker-delete-id="${image.id}" style="background:rgba(13,13,14,.78); color:#c96b6b; border:1px solid rgba(201,107,107,.32);">Delete</button>
                        ` : ''}
                    </div>
                </div>
            `;
        }).join('');

        this.libraryEmpty.classList.toggle('d-none', this.images.length > 0);

        this.libraryGrid.querySelectorAll('[data-picker-select-id]').forEach((button) => {
            button.addEventListener('click', () => {
                const image = this.images.find((item) => item.id === Number(button.dataset.pickerSelectId));

                if (!image) {
                    return;
                }

                this.toggleImageSelection(image);
            });
        });

        this.libraryGrid.querySelectorAll('[data-picker-delete-id]').forEach((button) => {
            button.addEventListener('click', async (event) => {
                event.stopPropagation();
                await this.handleLibraryDelete(Number(button.dataset.pickerDeleteId));
            });
        });
    }

    async fetchLibrary() {
        const url = new URL(this.libraryUrl, window.location.origin);

        if (this.searchTerm) {
            url.searchParams.set('search', this.searchTerm);
        }

        const response = await fetch(url, {
            headers: {
                Accept: 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Could not load the media library.');
        }

        const payload = await response.json();

        return payload.images || [];
    }

    async loadLibrary() {
        this.setLibraryError('');
        this.setLibraryBusy(true);

        try {
            this.images = await this.fetchLibrary();
            this.renderLibrary();
        } catch (error) {
            this.setLibraryError(error.message || 'Could not load the media library.');
        } finally {
            this.setLibraryBusy(false);
        }
    }

    async openLibrary() {
        this.modal.show();
        await this.loadLibrary();
    }

    async uploadFile(file) {
        const formData = new FormData();
        formData.append('image', file);

        const response = await fetch(this.uploadUrl, {
            method: 'POST',
            headers: {
                'X-CSRF-TOKEN': this.csrfToken,
                Accept: 'application/json',
            },
            body: formData,
        });

        if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload.message || 'Upload failed.');
        }

        const payload = await response.json();

        return payload.image;
    }

    async deleteLibraryImage(imageId) {
        const response = await fetch(this.deleteUrlTemplate.replace('__MEDIA_ID__', imageId), {
            method: 'DELETE',
            headers: {
                'X-CSRF-TOKEN': this.csrfToken,
                Accept: 'application/json',
            },
        });

        if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload.message || 'Could not delete the image.');
        }
    }

    async handleUpload(file) {
        if (!file) {
            return;
        }

        this.setError('');
        this.setLibraryError('');
        this.setBusy(true);
        this.setLibraryBusy(true);

        try {
            const image = await this.uploadFile(file);

            if (this.multiple) {
                if (!this.isSelected(image.id)) {
                    this.selectedImages = [...this.selectedImages, image];
                }
            } else {
                this.selectedImage = image;
            }

            this.render();
            await this.loadLibrary();
        } catch (error) {
            this.setLibraryError(error.message || 'Upload failed.');
        } finally {
            if (this.libraryInput) {
                this.libraryInput.value = '';
            }

            this.setBusy(false);
            this.setLibraryBusy(false);
        }
    }

    async handleLibraryDelete(imageId) {
        this.setLibraryError('');
        this.setLibraryBusy(true);

        try {
            await this.deleteLibraryImage(imageId);
            this.images = this.images.filter((image) => image.id !== imageId);

            if (this.multiple) {
                this.selectedImages = this.selectedImages.filter((image) => image.id !== imageId);
            } else if (this.selectedImage?.id === imageId) {
                this.selectedImage = null;
            }

            this.render();
            this.renderLibrary();
        } catch (error) {
            this.setLibraryError(error.message || 'Could not delete the image.');
        } finally {
            this.setLibraryBusy(false);
        }
    }

    clearSelection() {
        this.setSelection(this.multiple ? [] : null);
        this.setError('');
        this.render();
    }

    getValue() {
        return this.getSelection();
    }
}

export function initImagePickers(selector = '[data-image-picker]') {
    return [...document.querySelectorAll(selector)].map((root) => {
        if (root.imagePicker instanceof ImagePicker) {
            return root.imagePicker;
        }

        return new ImagePicker(root);
    });
}
